import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { airportTrip, describeStatus } from '@rydres/shared';
import { Badge, Button, Card, Confirm, InfoTip, Input, Notice, fmtDateTime, fmtPhone, formatUsd, DirectionsLinks, MapPreview, driveText } from '@rydres/ui';
import { chatApi, driverApi, remindersApi } from '../lib/api.js';
import { useOpenChat } from '../lib/chat.js';
import { useProfile } from '../lib/profile.js';
import type { Booking, BookingDetail, IncomingReferral } from '../lib/types.js';
import { ReferralRow } from './Referral.js';
import { CalendarView } from './Calendar.js';
import { BookingRow } from './Today.js';

const TABS = ['requests', 'upcoming', 'past', 'calendar'] as const;
type Tab = (typeof TABS)[number];

/** Riders waiting for an answer: a request, or a price they have not answered yet. An unpaid card booking waits for the rider's payment alone. */
const isWaiting = (b: Booking) => ((b.status === 'requested' && b.paymentMode !== 'card') || b.status === 'quoted' || b.status === 'referred') && Date.parse(b.startsAt) >= Date.now() - 6 * 3_600_000;

/** Why the time needs a second look before saying yes. */
const CLASH_TEXT: Record<NonNullable<BookingDetail['clash']>, string> = {
  booked: 'You already have a confirmed ride at this time.',
  hours: 'This pickup is outside your hours.',
  block: 'You marked this time off, or your phone calendar shows it busy.',
  accepted: 'You have sent a price for another ride at this time. The first rider to answer gets the time.',
};

export function Bookings() {
  const { profile } = useProfile();
  const [params, setParams] = useSearchParams();
  const [waiting, setWaiting] = useState<Booking[] | null>(null);
  const [referred, setReferred] = useState<IncomingReferral[]>([]);
  const tab: Tab = TABS.find((t) => t === params.get('view')) ?? 'upcoming';
  const setTab = (t: Tab) => setParams(t === 'upcoming' ? {} : { view: t }, { replace: true });
  const [rows, setRows] = useState<Booking[] | null>(null);
  useEffect(() => {
    driverApi.bookings({ from: new Date(Date.now() - 6 * 3_600_000).toISOString(), status: 'requested,quoted,referred' }).then((r) => setWaiting(r.bookings.filter(isWaiting)), () => setWaiting([]));
    driverApi.referrals().then((r) => setReferred(r.referrals), () => setReferred([]));
  }, [tab]);
  useEffect(() => {
    if (tab === 'calendar' || tab === 'requests') return;
    setRows(null);
    const now = new Date().toISOString();
    const q = tab === 'upcoming' ? { from: now } : { to: now };
    driverApi.bookings(q).then((r) => setRows(tab === 'past' ? [...r.bookings].reverse() : r.bookings), () => setRows([]));
  }, [tab]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black">Bookings</h1>
        <Link to="/bookings/availability" className="rounded-xl bg-card2 px-3 py-2 text-sm font-bold">Hours & time off</Link>
      </div>
      <div className="flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`flex-auto whitespace-nowrap rounded-full px-2 py-1.5 capitalize ${tab === t ? 'bg-card shadow-card' : 'text-muted'}`}>
            {t}{t === 'requests' && (waiting?.length ?? 0) + referred.length ? ` (${(waiting?.length ?? 0) + referred.length})` : ''}
          </button>
        ))}
      </div>
      {tab === 'calendar' ? (
        <CalendarView key={params.get('day') ?? ''} initialDay={params.get('day') ?? undefined} />
      ) : tab === 'requests' ? (
        waiting === null ? <p className="text-sm text-muted">Loading…</p> : waiting.length + referred.length === 0 ? <p className="text-sm text-muted">No requests waiting for you.</p> : (
          <div className="space-y-2">
            {referred.map((r) => <ReferralRow key={r.id} r={r} tz={profile?.timezone} />)}
            {waiting.map((b) => <BookingRow key={b.id} b={b} tz={profile?.timezone} />)}
          </div>
        )
      ) : rows === null ? <p className="text-sm text-muted">Loading…</p> : rows.length === 0 ? <p className="text-sm text-muted">Nothing here yet.</p> : (
        <div className="space-y-2">{rows.map((b) => <BookingRow key={b.id} b={b} tz={profile?.timezone} />)}</div>
      )}
    </div>
  );
}

export function BookingDetailScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const openChat = useOpenChat();
  const { profile } = useProfile();
  const [b, setB] = useState<BookingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | 'cancel' | 'decline' | 'complete' | 'no-show' | 'refund' | 'quote' | 'refer'>(null);
  const [backups, setBackups] = useState<Awaited<ReturnType<typeof driverApi.referralCandidates>>['drivers'] | null>(null);
  const openRefer = () => {
    setBackups(null);
    setDialog('refer');
    driverApi.referralCandidates().then((r) => setBackups(r.drivers), () => setBackups([]));
  };
  const [refundInput, setRefundInput] = useState('');
  const [quote, setQuote] = useState({ price: '', hours: '', message: '' });
  const [trip, setTrip] = useState<Awaited<ReturnType<typeof driverApi.route>> | null>(null);
  const [suggested, setSuggested] = useState<{ cents: number } | 'no-rates' | null>(null);
  useEffect(() => {
    driverApi.route(id).then(setTrip, () => setTrip(null));
  }, [id]);
  // The quote opens with the drive's length and, when the driver has saved rates, their price for it.
  const openQuote = () => {
    const drive = trip?.route;
    const saved = b?.suggestedService;
    setQuote({
      price: b?.priceCents ? String(b.priceCents / 100) : saved ? String(saved.priceCents / 100) : '',
      hours: saved ? String(saved.durationMin / 60) : drive ? String(Math.max(0.5, Math.ceil(drive.minutes / 30) / 2)) : '',
      message: '',
    });
    setSuggested(null);
    setDialog('quote');
    if (drive)
      void remindersApi.rateCard().then(async (card) => {
        if (!card.saved) return setSuggested('no-rates');
        const airport = airportTrip([{ address: b?.pickupAddress ?? '', point: trip?.from }, { address: b?.dropoffAddress ?? '', point: trip?.to }]);
        const e = await remindersApi.estimate({ mode: 'distance', miles: drive.miles, minutes: drive.minutes, airport, waitMinutes: 0, roundToDollars: 5 });
        setSuggested({ cents: e.totalCents });
      }, () => undefined);
  };
  const tz = profile?.timezone;
  const load = () => driverApi.booking(id).then(setB, () => navigate('/bookings'));
  useEffect(() => void load(), [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (action: 'confirm' | 'decline' | 'complete' | 'paid' | 'no-show' | 'cancel' | 'refund', body?: Record<string, unknown>) => {
    setError(null);
    try {
      setB(await driverApi.act(id, action, body));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
    setDialog(null);
  };
  if (!b) return <p className="text-sm text-muted">Loading…</p>;
  const paid = b.payment?.status === 'succeeded' || b.payment?.status === 'partially_refunded';
  const remaining = b.payment ? b.payment.amountCents - b.payment.refundedCents : 0;
  const look = describeStatus(b);
  const pending = b.status === 'requested' && b.paymentMode !== 'card';
  const direct = b.paymentMode !== 'card';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-full bg-card2 p-2" aria-label="Back">‹</button>
        <h1 className="text-xl font-black">{fmtDateTime(b.startsAt, tz)}</h1>
        <Badge value={look.label} tone={look.tone} />
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      {b.canMessage && (
        <button
          type="button"
          onClick={() => void chatApi.open({ bookingId: b.id }).then((c) => openChat(c.id), (err) => setError(err instanceof ApiError ? err.message : 'Could not open the conversation.'))}
          className="rounded-xl bg-card2 px-3 py-2 text-sm font-bold"
        >
          Message {b.riderName}
        </button>
      )}
      {pending && b.kind === 'custom' && (
        <Notice tone="warn">{b.riderName} asked for this ride. Accept and name your price, and they get to accept and pay.</Notice>
      )}
      {b.status === 'quoted' && (
        <Notice tone="info">You accepted at this price{b.quoteExpiresAt ? `; it is open until ${fmtDateTime(b.quoteExpiresAt, tz)}` : ''}. It is confirmed once {b.riderName} answers. You can send a new price if they ask.</Notice>
      )}
      {pending && b.kind !== 'custom' && (
        <Notice tone="warn">{b.riderName} wants to pay you directly. Accept to lock the time and let them know.</Notice>
      )}
      {b.clash && <Notice tone="warn">{CLASH_TEXT[b.clash]}</Notice>}
      {b.status === 'referred' && b.referral && (
        <Notice tone="info">
          With {b.referral.toDriver.displayName ?? b.referral.toDriver.legalName}{b.referral.status === 'waiting_rider' ? ', who can take it. It is up to the rider now' : ', waiting for their answer'}. If it goes no further it comes back to you.
        </Notice>
      )}
      {b.status === 'handed_off' && b.referral && <Notice tone="info">The rider accepted {b.referral.toDriver.displayName ?? b.referral.toDriver.legalName}, so this ride is now theirs.</Notice>}
      <Card>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted">Rider</dt>
          <dd className="font-bold">{b.riderName} · {b.passengers} pax{b.riderPhone && <> · <a className="text-accent-text underline" href={`tel:${b.riderPhone}`}>{fmtPhone(b.riderPhone)}</a></>}</dd>
          <dt className="text-muted">From</dt><dd>{b.pickupAddress}</dd>
          <dt className="text-muted">To</dt><dd>{b.dropoffAddress}</dd>
          {b.service && <><dt className="text-muted">Service</dt><dd>{b.service.name}</dd></>}
          <dt className="text-muted">Price</dt>
          <dd className="font-bold">{b.priceCents != null ? <>{formatUsd(b.priceCents)} <span className="font-medium text-ink2">· {b.paymentMode === 'card' ? `card${b.feeCents ? ` (${formatUsd(b.feeCents)} platform fee)` : ''}` : b.paymentMode}</span></> : <span className="font-medium text-ink2">Not set yet</span>}</dd>
          {b.status === 'completed' && direct && <><dt className="text-muted">Payment</dt><dd>{b.directPaidAt ? `Received · ${fmtDateTime(b.directPaidAt, tz)}` : 'Not received yet'}</dd></>}
          {b.payment && <><dt className="text-muted">Payment</dt><dd>{b.payment.status.replace('_', ' ')}{b.payment.refundedCents ? ` · ${formatUsd(b.payment.refundedCents)} refunded` : ''}{b.payment.receiptUrl && <> · <a className="text-accent-text underline" href={b.payment.receiptUrl} target="_blank" rel="noreferrer">receipt</a></>}</dd></>}
          {b.notes && <><dt className="text-muted">Notes</dt><dd>{b.notes}</dd></>}
          <dt className="text-muted">Arranged</dt><dd className="text-ink2">{fmtDateTime(b.arrangedAt, tz)} · {b.arrangedVia}</dd>
          {b.cancelReason && <><dt className="text-muted">{b.status === 'declined' ? 'Declined' : 'Cancelled'}</dt><dd>{b.cancelReason}{b.status === 'declined' ? '' : ` (${b.cancelledBy})`}</dd></>}
          {b.quoteUrl && b.status === 'quoted' && (
            <>
              <dt className="flex items-start gap-1 text-muted">Quote link<InfoTip label="Quote link">The rider is texted this link automatically; use these if you want to send it yourself too.</InfoTip></dt>
              <dd className="space-y-1">
                <div className="break-all text-xs text-ink2">{b.quoteUrl}</div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => void navigator.clipboard?.writeText(b.quoteUrl!)} className="rounded-xl bg-card2 px-3 py-1.5 text-xs font-bold">Copy link</button>
                  {b.riderPhone && (
                    <a className="rounded-xl bg-card2 px-3 py-1.5 text-xs font-bold" href={`sms:${b.riderPhone}?&body=${encodeURIComponent(`Here is your quote from ${profile?.displayName ?? 'your driver'}: ${b.quoteUrl}`)}`}>Text it to the rider</a>
                  )}
                  {typeof navigator.share === 'function' && (
                    <button onClick={() => void navigator.share({ url: b.quoteUrl!, text: 'Your quote' })} className="rounded-xl bg-card2 px-3 py-1.5 text-xs font-bold">Share…</button>
                  )}
                </div>
              </dd>
            </>
          )}
        </dl>
      </Card>

      {trip && (trip.from || trip.to) && (
        <Card>
          <MapPreview from={trip.from} to={trip.to} line={trip.route?.polyline} />
          {trip.route && <p className="mt-2 text-xs text-muted">{driveText(trip.route)}{trip.located ? '. Found from the typed addresses, so check the pins.' : ''}</p>}
          <div className="mt-3 space-y-2">
            <DirectionsLinks label="To pickup" address={b.pickupAddress} point={b.pickupPoint} />
            <DirectionsLinks label="To drop-off" address={b.dropoffAddress} point={b.dropoffPoint} />
          </div>
        </Card>
      )}

      {b.waybill && (
        <Card title={`Waybill v${b.waybill.version}`} info="Show the public link at an airport or roadside check. The PDF opens best in Safari or Chrome.">
          <div className="flex flex-wrap gap-2">
            <a href={b.waybill.shareUrl} target="_blank" rel="noreferrer" className="rounded-xl bg-brand px-4 py-2 text-sm font-bold text-on-brand brand-press">Open PDF</a>
            <button onClick={() => void navigator.clipboard?.writeText(b.waybill!.shareUrl)} className="rounded-xl bg-card2 px-4 py-2 text-sm font-bold">Copy link</button>
          </div>
        </Card>
      )}

      {(b.status === 'confirmed' || b.status === 'quoted' || b.status === 'requested') && (
        <a href={b.googleCalendarUrl} target="_blank" rel="noreferrer" className="block text-center text-sm font-bold text-accent-text underline">Add to Google Calendar</a>
      )}
      <div className="grid gap-2">
        {b.kind === 'custom' && (b.status === 'requested' || b.status === 'quoted') && (
          <Button onClick={openQuote}>{b.status === 'quoted' ? 'Send a new price' : 'Accept'}</Button>
        )}
        {pending && b.kind !== 'custom' && <Button onClick={() => void act('confirm')}>Accept</Button>}
        {b.canRefer && <Button variant="ghost" onClick={openRefer}>Refer to another driver</Button>}
        {b.status === 'referred' && <Button variant="ghost" onClick={() => void driverApi.withdrawReferral(id).then(setB, (err) => setError(err instanceof ApiError ? err.message : 'That did not work.'))}>Take it back</Button>}
        {pending && <Button variant="danger" onClick={() => setDialog('decline')}>Decline</Button>}
        {b.status === 'confirmed' && <Button onClick={() => (direct ? setDialog('complete') : void act('complete'))}>Mark completed</Button>}
        {b.status === 'confirmed' && <Button variant="ghost" onClick={() => setDialog('no-show')}>Rider did not show</Button>}
        {b.status === 'completed' && direct && !b.directPaidAt && <Button onClick={() => void act('paid', { paid: true })}>Mark as paid</Button>}
        {(b.status === 'confirmed' || b.status === 'quoted' || (b.status === 'requested' && !pending)) && <Button variant="danger" onClick={() => setDialog('cancel')}>Cancel booking</Button>}
        {paid && remaining > 0 && (b.status === 'completed' || b.status === 'no_show' || b.status === 'cancelled') && (
          <Button variant="ghost" onClick={() => { setRefundInput(String(remaining / 100)); setDialog('refund'); }}>Refund the rider</Button>
        )}
      </div>

      {dialog === 'refer' && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center">
          <div className="w-full max-w-md space-y-3 rounded-3xl border border-line bg-card p-5">
            <div className="text-lg font-black">Refer to another driver</div>
            <p className="text-sm text-ink2">They see the time, addresses, passengers and instructions, not the rider's name or number. They name their own price and the rider decides. No money or fee passes between you.</p>
            {backups === null ? (
              <p className="text-sm text-muted">Loading…</p>
            ) : backups.length === 0 ? (
              <p className="text-sm text-ink2">You have no backup drivers taking rides. Add them under People.</p>
            ) : (
              <div className="grid gap-2">
                {backups.map((d) => (
                  <button key={d.id} onClick={() => void driverApi.refer(id, d.id).then((next) => { setB(next); setDialog(null); }, (err) => { setError(err instanceof ApiError ? err.message : 'That did not work.'); setDialog(null); })} className="flex items-center justify-between gap-3 rounded-xl bg-card2 px-4 py-3 text-left font-bold">
                    <span className="min-w-0">{d.displayName ?? d.legalName}<span className="block text-xs font-medium text-muted">{d.tcpNumber}</span></span>
                    <span className="text-muted">›</span>
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" className="w-full" onClick={() => setDialog(null)}>Back</Button>
          </div>
        </div>
      )}
      {dialog === 'decline' && (
        <Confirm
          title="Decline this request?"
          body={`${b.riderName} is told you cannot take the ride.`}
          confirmLabel="Decline"
          danger
          withReason
          onConfirm={(reason) => void act('decline', { reason: reason || undefined })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'complete' && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center">
          <div className="w-full max-w-md space-y-3 rounded-3xl border border-line bg-card p-5">
            <div className="text-lg font-black">Did {b.riderName} pay you?</div>
            <p className="text-sm text-ink2">{b.priceCents != null ? `${formatUsd(b.priceCents)}, paid to you directly. ` : ''}Your records and their payment history show what you say here.</p>
            <div className="grid gap-2">
              <Button onClick={() => void act('complete', { paid: true })}>Yes, I was paid</Button>
              <Button variant="ghost" onClick={() => void act('complete', { paid: false })}>Not yet</Button>
              <Button variant="ghost" onClick={() => setDialog(null)}>Back</Button>
            </div>
          </div>
        </div>
      )}
      {dialog === 'cancel' && (
        <Confirm
          title="Cancel this booking?"
          body={paid ? `The rider gets a full refund of ${formatUsd(remaining)} because you are cancelling.` : 'The rider will be texted.'}
          confirmLabel="Cancel booking"
          danger
          withReason
          onConfirm={(reason) => void act('cancel', { reason: reason || undefined })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'no-show' && (
        <Confirm
          title="Mark as no-show?"
          body={paid && b.suggestedRefunds ? `Your policy keeps ${b.suggestedRefunds.noShow.keptPct}%; ${formatUsd(b.suggestedRefunds.noShow.refundCents)} goes back to the rider.` : 'The rider will be texted.'}
          confirmLabel="No-show"
          danger
          onConfirm={() => void act('no-show')}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog === 'quote' && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center">
          <div className="w-full max-w-md space-y-3 rounded-3xl border border-line bg-card p-5">
            <div className="text-lg font-black">{b.status === 'quoted' ? 'A new price for this trip' : 'Accept: your price for this trip'}</div>
            <p className="text-sm text-ink2 whitespace-pre-line">{b.notes || 'No extra details from the rider. Price it from the pickup, drop-off, time and passenger count, or call them.'}</p>
            {trip?.route && <p className="text-xs text-muted">{driveText(trip.route)}</p>}
            {b.suggestedService && <p className="text-xs text-muted">Filled in from your service "{b.suggestedService.name}", {formatUsd(b.suggestedService.priceCents)}.</p>}
            {suggested === 'no-rates' && <p className="text-xs text-muted">Save your rates in the <Link to="/calculator" className="font-bold text-accent-text underline">price calculator</Link> to get a suggested price here.</p>}
            {suggested && suggested !== 'no-rates' && (
              <div className="flex items-center justify-between gap-2 rounded-xl bg-card2 px-3 py-2 text-sm">
                <span>From your rates: <strong>{formatUsd(suggested.cents)}</strong></span>
                <button type="button" className="font-bold text-accent-text underline" onClick={() => setQuote((x) => ({ ...x, price: String(suggested.cents / 100) }))}>Use it</button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Price $ (per vehicle)</span><Input type="number" step="1" min="1" autoFocus value={quote.price} onChange={(e) => setQuote({ ...quote, price: e.target.value })} /></label>
              <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Trip length (hours)</span><Input type="number" step="0.5" min="0.5" placeholder="1" value={quote.hours} onChange={(e) => setQuote({ ...quote, hours: e.target.value })} /></label>
            </div>
            <Input placeholder="Message to the rider (optional)" value={quote.message} onChange={(e) => setQuote({ ...quote, message: e.target.value })} />
            <p className="text-xs text-muted">The rider gets to accept and pay. Your price stays open for 24 hours, or until 2 hours before pickup. <Link to="/calculator" className="font-bold text-accent-text underline">Open the calculator</Link></p>
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => setDialog(null)}>Back</Button>
              <Button className="flex-1" disabled={!quote.price} onClick={async () => {
                setError(null);
                try {
                  setB(await driverApi.quote(id, { priceCents: Math.round(Number(quote.price) * 100), ...(quote.hours ? { durationMin: Math.round(Number(quote.hours) * 60) } : {}), message: quote.message || null }));
                  setDialog(null);
                } catch (err) { setError(err instanceof ApiError ? err.message : 'Could not send the price.'); }
              }}>{b.status === 'quoted' ? 'Send new price' : 'Accept at this price'}</Button>
            </div>
          </div>
        </div>
      )}
      {dialog === 'refund' && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center">
          <div className="w-full max-w-md space-y-3 rounded-3xl border border-line bg-card p-5">
            <div className="text-lg font-black">Refund amount</div>
            <Input type="number" step="0.01" min="0.01" max={remaining / 100} value={refundInput} onChange={(e) => setRefundInput(e.target.value)} />
            <p className="text-xs text-muted">Up to {formatUsd(remaining)}. Card processing fees are not returned to you.</p>
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => setDialog(null)}>Back</Button>
              <Button className="flex-1" onClick={() => void act('refund', { amountCents: Math.round(Number(refundInput) * 100) })}>Refund</Button>
            </div>
          </div>
        </div>
      )}
      <Link to="/bookings" className="block text-center text-sm text-muted">All bookings</Link>
    </div>
  );
}
